import { api } from './api.ts'
import { toApiError } from './api-error.ts'
import type { Theme } from './theme.ts'

/** One page on a TV. An entry stored with no type is a page. */
export type PlaylistPage = {
  type?: 'page'
  path: string
  seconds: number
}

/** One dashboard on a TV. */
export type PlaylistDashboard = {
  type: 'dashboard'
  dashboardId: string
  seconds: number
}

/** One playlist entry, as the service stores it. */
export type PlaylistEntry = PlaylistPage | PlaylistDashboard | PlaylistVideo

/** One validated YouTube video on a TV. */
export type PlaylistVideo = {
  type: 'video'
  videoId: string
  seconds: number
  /** Absent on an older stored entry. Playback and the editor default to muted. */
  sound?: boolean
}

/** One configured TV. */
export type DisplayRecord = {
  name: string
  pages: PlaylistEntry[]
  /** Absent on a document stored before themes. The editor treats that as dark. */
  theme?: Theme
  updatedBy: string | null
  updatedAt: string | null
}

/**
 * A page a TV may show.
 *
 * The service sends no title. The page registry has none; the screen matches
 * `id` to the nav title.
 */
export type DisplayablePage = {
  id: string
  path: string
}

/** A Studio dashboard shared with the company. */
export type SharedDashboard = {
  id: string
  title: string
}

/** What a playing screen says it is showing. */
export type DisplayShowing = {
  path: string
  index: number
  count: number
}

/** One office screen, as last reported. */
export type DisplayScreen = {
  viewport?: { width: number; height: number } | null
  bundle?: string | null
  deviceId: string
  name: string
  state: 'starting' | 'playing' | 'not-set-up'
  showing: DisplayShowing | null
  signedInAs: string
  userAgent: string | null
  firstSeenAt: string
  lastSeenAt: string
  online: boolean
  playlist: boolean
}

/** `GET /displays`. */
export type DisplaysResponse = {
  reloads?: Record<string, { requestedAt: string; requestedBy: string }>
  displays: DisplayRecord[]
  pages: DisplayablePage[]
  dashboards: SharedDashboard[]
  /** False when the service could not list every shared dashboard. */
  dashboardsComplete: boolean
  screens: DisplayScreen[]
  /** False when the service could not list every screen. */
  screensComplete: boolean
}

/**
 * How often the Displays page reads the list again.
 *
 * A product decision. Fifteen seconds keeps Online and Offline current
 * between the TVs' one-minute reports. `displays.test.ts` pins the value.
 */
export const DISPLAYS_REFRESH_MS = 15_000

/** Query key for the Displays page. */
export const DISPLAYS_QUERY_KEY = ['displays'] as const

/**
 * Read every TV and the pages a playlist may use.
 *
 * @returns The Displays page payload.
 */
export async function fetchDisplays(): Promise<DisplaysResponse> {
  try {
    return await api.get('displays').json<DisplaysResponse>()
  } catch (error) {
    throw await toApiError(error)
  }
}
